import React from 'react';
import { Search, Shield, BarChart2, Sparkles, ChevronRight, Award } from 'lucide-react';

const CtaSectionRedesigned: React.FC = () => {
  const company = "Silver International SA";

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Option 3 Redesignée - Tech Dashboard</h1>
            <p className="text-gray-600 mt-2">Adaptée aux couleurs de TestPage.tsx (fond blanc, cards grises foncées, accents orange)</p>
          </div>
        </div>
      </header>

      <div className="py-12">
        
        {/* Version Originale pour Comparaison */}
        <div className="bg-white mb-16">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-500 mb-2">Version Originale (pour comparaison)</h2>
            </div>
            
            {/* CTA Section - Version Originale */}
            <section className="relative bg-gray-50 py-16 border border-gray-300 rounded-lg opacity-50">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full text-blue-700 font-medium text-sm mb-6">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        Analyse en temps réel
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                        Optimisez les performances de <span className="text-blue-600">{company}</span>
                      </h2>
                      <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg">
                        Lancer l'analyse complète
                      </button>
                    </div>
                    <div>
                      <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                        <div className="text-sm text-gray-500">Dashboard preview...</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Version Redesignée avec les couleurs de TestPage */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Version Redesignée - Couleurs TestPage</h2>
              <p className="text-gray-600">Fond blanc, cards grises foncées (gray-800), accents orange</p>
            </div>
            
            {/* CTA Section - Style TestPage avec Tech Dashboard */}
            <section className="relative bg-white py-16 border-2 border-orange-200 rounded-lg">
              <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    
                    {/* Left side - Content */}
                    <div>
                      {/* Status badge - Style TestPage */}
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                        <Award className="w-4 h-4 mr-2" />
                        Analyse digitale gratuite
                      </div>
                      
                      {/* Title - Style TestPage */}
                      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                        Envie d'aller plus loin avec 
                        <span className="text-orange-600"> {company}</span> ?
                      </h2>
                      
                      {/* Subtitle */}
                      <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                        Affinez votre feuille de route digitale avec un diagnostic complet et des recommandations personnalisées.
                      </p>
                      
                      {/* Features list - Style TestPage */}
                      <div className="space-y-4 mb-8">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <Search className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">7 questions rapides</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <Shield className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">Analyse sécurisée</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                            <BarChart2 className="w-4 h-4" />
                          </div>
                          <span className="text-gray-700 font-medium">Résultats instantanés</span>
                        </div>
                      </div>
                      
                      {/* CTA Button - Style TestPage */}
                      <button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-2xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 mb-6">
                        <span className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5" />
                          Obtenir mon score en 1 minute
                          <ChevronRight className="w-5 h-5" />
                        </span>
                      </button>
                      
                      {/* Trust indicators - Style TestPage */}
                      <div className="flex justify-center lg:justify-start items-center space-x-6 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Sans engagement
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          Données sécurisées
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          Résultats immédiats
                        </span>
                      </div>
                    </div>
                    
                    {/* Right side - Dashboard Preview - Style TestPage (card grise foncée) */}
                    <div>
                      <div className="bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-700">
                        {/* Header - Style TestPage */}
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <span className="font-semibold text-white">{company}</span>
                          </div>
                          <span className="text-xs text-orange-400 bg-orange-500/20 px-2 py-1 rounded-full">Live</span>
                        </div>
                        
                        {/* Metrics - Style TestPage avec accents orange */}
                        <div className="space-y-6">
                          {/* SEO Score */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-300">Score SEO</span>
                              <span className="text-lg font-bold text-white">75%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-3">
                              <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full transition-all duration-1000" style={{width: '75%'}}></div>
                            </div>
                          </div>
                          
                          {/* Performance */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-300">Performance</span>
                              <span className="text-lg font-bold text-white">82%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-3">
                              <div className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-1000" style={{width: '82%'}}></div>
                            </div>
                          </div>
                          
                          {/* UX Score */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-300">Expérience utilisateur</span>
                              <span className="text-lg font-bold text-white">68%</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-3">
                              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-3 rounded-full transition-all duration-1000" style={{width: '68%'}}></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Quick stats */}
                        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-700">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-orange-400">127</div>
                            <div className="text-xs text-gray-400">Mots-clés</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-400">94%</div>
                            <div className="text-xs text-gray-400">Uptime</div>
                          </div>
                        </div>
                        
                        {/* Footer - Style TestPage */}
                        <div className="mt-6 pt-4 border-t border-gray-700">
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <div className="p-1 rounded bg-orange-500/20">
                              <Award className="w-3 h-3 text-orange-400" />
                            </div>
                            <span>Dernière analyse: il y a 2 minutes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Comparaison des couleurs */}
        <div className="bg-gray-50 py-12">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Palette de Couleurs Utilisée</h3>
            
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-white border-2 border-gray-200 rounded-lg mx-auto mb-3"></div>
                <h4 className="font-semibold text-gray-900">Fond Principal</h4>
                <p className="text-sm text-gray-600">bg-white</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-gray-800 rounded-lg mx-auto mb-3"></div>
                <h4 className="font-semibold text-gray-900">Cards Principales</h4>
                <p className="text-sm text-gray-600">bg-gray-800</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-orange-500 rounded-lg mx-auto mb-3"></div>
                <h4 className="font-semibold text-gray-900">Accent Principal</h4>
                <p className="text-sm text-gray-600">bg-orange-500</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-lg mx-auto mb-3"></div>
                <h4 className="font-semibold text-gray-900">Accent Subtil</h4>
                <p className="text-sm text-gray-600">bg-orange-100</p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-white rounded-xl border border-orange-200 max-w-2xl mx-auto">
              <h5 className="font-semibold mb-2 text-gray-900">✨ Cohérence avec TestPage.tsx</h5>
              <p className="text-sm text-gray-600">
                Cette version respecte parfaitement la charte graphique de TestPage : fond blanc, cards grises foncées (gray-800), 
                accents orange pour les CTAs et éléments importants, avec des badges orange subtils (orange-100).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CtaSectionRedesigned;
